import { Check } from 'lucide-react'
import { Link } from 'react-router-dom'
import { cn } from '@/lib/cn'

const STEPS = ['Cart', 'Date', 'Delivery', 'Payment']

/**
 * The four-step order journey: 1 Cart, 2 Date, 3 Delivery, 4 Payment.
 * Monochrome circles with the single blue accent on the current step. Below
 * sm every label sits ABOVE its circle (compact columns fit 375px); from sm
 * up the label sits beside it. Fixed-width connectors keep the spacing
 * symmetric regardless of label widths. Completed steps stay clickable to go
 * back: step 1 links to the cart, steps 2 and 3 call onStepClick so later
 * screens can return without losing anything.
 */
export default function CheckoutSteps({
  current,
  onStepClick,
}: {
  current: 1 | 2 | 3 | 4
  onStepClick?: (step: 2 | 3) => void
}) {
  return (
    <div className="flex w-full items-end justify-center sm:items-center">
      {STEPS.map((label, index) => {
        const step = index + 1
        const done = step < current
        const active = step === current

        const circle = (
          <span
            className={cn(
              'flex size-7 shrink-0 items-center justify-center rounded-full text-xs font-bold',
              done && 'bg-foreground text-background',
              active && 'bg-primary text-white',
              !done && !active && 'border border-foreground/20 text-foreground/50',
            )}
          >
            {done ? <Check className="size-4" /> : step}
          </span>
        )

        const labelClass = cn(
          'font-semibold uppercase tracking-wide',
          active ? 'text-foreground' : 'text-foreground/50',
        )

        // The label renders twice: a compact one above the circle on mobile,
        // a text-xs one beside it from sm up.
        const inner = (
          <>
            <span className={cn(labelClass, 'text-2xs sm:hidden')}>{label}</span>
            {circle}
            <span className={cn(labelClass, 'hidden text-xs sm:inline')}>{label}</span>
          </>
        )
        const innerClass = 'flex flex-col items-center gap-1 sm:flex-row sm:gap-2'

        let content = <span className={innerClass}>{inner}</span>
        if (step === 1 && current > 1) {
          content = (
            <Link to="/cart" className={cn(innerClass, 'transition-opacity hover:opacity-75')} title="Back to cart">
              {inner}
            </Link>
          )
        } else if ((step === 2 || step === 3) && done && onStepClick) {
          content = (
            <button
              type="button"
              onClick={() => onStepClick(step as 2 | 3)}
              className={cn(innerClass, 'transition-opacity hover:opacity-75')}
              title={`Back to ${label.toLowerCase()}`}
            >
              {inner}
            </button>
          )
        }

        return (
          <div key={label} className="flex items-end sm:items-center">
            {index > 0 && (
              // mb centers the line on the 28px circle while labels sit above
              // it on mobile; from sm the row itself is centered.
              <span
                className={cn(
                  'mx-2 mb-3.5 h-px w-5 sm:mx-3 sm:mb-0 sm:w-9',
                  done || active ? 'bg-foreground/40' : 'bg-foreground/15',
                )}
              />
            )}
            {content}
          </div>
        )
      })}
    </div>
  )
}
